import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
    ArrowLeft, Search, BookOpen, Heart, Brain, Bone, Activity,
    Pill, Stethoscope, FileText, ExternalLink, ChevronRight
} from 'lucide-react';

// Patient Information Leaflets Data
const conditionLeaflets = {
    cardiovascular: [
        {
            id: 'hypertension',
            title: 'High Blood Pressure (Hypertension)',
            summary: 'Understanding and managing high blood pressure',
            content: {
                overview: 'High blood pressure (hypertension) is when the force of blood against your artery walls is consistently too high. It affects millions worldwide and is a major risk factor for heart disease and stroke.',
                symptoms: ['Often no symptoms (silent killer)', 'Headaches', 'Shortness of breath', 'Nosebleeds', 'Dizziness'],
                causes: ['Family history', 'Obesity', 'Lack of physical activity', 'High salt diet', 'Excessive alcohol', 'Stress', 'Age'],
                treatment: ['Lifestyle changes (diet, exercise)', 'Reducing salt intake', 'Limiting alcohol', 'Medications (ACE inhibitors, beta-blockers, diuretics)', 'Regular monitoring'],
                prevention: ['Maintain healthy weight', 'Exercise regularly', 'Eat a balanced diet', 'Reduce sodium intake', 'Limit alcohol', 'Manage stress'],
                whenToSeek: 'Seek immediate medical attention if you experience severe headache, chest pain, difficulty breathing, or vision problems.'
            }
        },
        {
            id: 'heart-disease',
            title: 'Coronary Heart Disease',
            summary: 'Information about heart disease and its management',
            content: {
                overview: 'Coronary heart disease (CHD) occurs when the coronary arteries become narrowed by a build-up of fatty material within their walls.',
                symptoms: ['Chest pain (angina)', 'Shortness of breath', 'Pain in neck, jaw, or back', 'Fatigue', 'Heart palpitations'],
                causes: ['High cholesterol', 'High blood pressure', 'Smoking', 'Diabetes', 'Obesity', 'Family history'],
                treatment: ['Lifestyle modifications', 'Medications (statins, aspirin, beta-blockers)', 'Angioplasty and stents', 'Coronary artery bypass surgery'],
                prevention: ['Stop smoking', 'Control cholesterol', 'Manage blood pressure', 'Exercise regularly', 'Maintain healthy weight', 'Manage diabetes'],
                whenToSeek: 'Call emergency services immediately if you experience chest pain, shortness of breath, or suspect a heart attack.'
            }
        }
    ],
    respiratory: [
        {
            id: 'asthma',
            title: 'Asthma',
            summary: 'Managing asthma and preventing attacks',
            content: {
                overview: 'Asthma is a long-term condition affecting the airways in the lungs. It causes breathing difficulties when airways become inflamed and narrow.',
                symptoms: ['Wheezing', 'Breathlessness', 'Tight chest', 'Coughing', 'Symptoms worse at night or early morning'],
                causes: ['Allergies (pollen, dust mites, pet dander)', 'Respiratory infections', 'Exercise', 'Cold air', 'Air pollutants', 'Stress'],
                treatment: ['Reliever inhalers (blue)', 'Preventer inhalers (brown/orange)', 'Combination inhalers', 'Leukotriene receptor antagonists', 'Asthma action plan'],
                prevention: ['Identify and avoid triggers', 'Take preventer medication regularly', 'Keep reliever inhaler handy', 'Get flu vaccination', 'Monitor peak flow'],
                whenToSeek: 'Seek emergency care if reliever inhaler is not helping, symptoms are severe, or you cannot speak in full sentences.'
            }
        },
        {
            id: 'copd',
            title: 'COPD (Chronic Obstructive Pulmonary Disease)',
            summary: 'Understanding and living with COPD',
            content: {
                overview: 'COPD is a group of lung conditions that cause breathing difficulties, including emphysema and chronic bronchitis.',
                symptoms: ['Increasing breathlessness', 'Persistent cough with phlegm', 'Frequent chest infections', 'Wheezing'],
                causes: ['Smoking (main cause)', 'Long-term exposure to fumes', 'Dust and chemicals', 'Genetic factors (alpha-1 antitrypsin deficiency)'],
                treatment: ['Stop smoking', 'Inhalers and medications', 'Pulmonary rehabilitation', 'Oxygen therapy', 'Surgery (in severe cases)'],
                prevention: ['Never start smoking or quit if you do', 'Avoid occupational exposure to lung irritants', 'Get vaccinated against flu and pneumonia'],
                whenToSeek: 'Seek urgent care if you experience severe breathlessness, bluish lips or fingers, or confusion.'
            }
        }
    ],
    endocrine: [
        {
            id: 'type2-diabetes',
            title: 'Type 2 Diabetes',
            summary: 'Managing blood sugar and preventing complications',
            content: {
                overview: 'Type 2 diabetes is a lifelong condition where the body cannot regulate blood sugar levels properly due to insulin resistance or insufficient insulin production.',
                symptoms: ['Increased thirst', 'Frequent urination', 'Fatigue', 'Blurred vision', 'Slow-healing wounds', 'Unexplained weight loss'],
                causes: ['Obesity', 'Sedentary lifestyle', 'Family history', 'Age (over 45)', 'Ethnicity', 'Gestational diabetes history'],
                treatment: ['Healthy eating', 'Regular exercise', 'Blood sugar monitoring', 'Medications (Metformin)', 'Insulin therapy (if needed)'],
                prevention: ['Maintain healthy weight', 'Stay physically active', 'Eat a balanced diet', 'Limit processed foods and sugar', 'Regular health checks'],
                whenToSeek: 'Seek immediate care if you experience symptoms of diabetic ketoacidosis: excessive thirst, frequent urination, nausea, abdominal pain, or fruity-smelling breath.'
            }
        },
        {
            id: 'thyroid',
            title: 'Thyroid Disorders',
            summary: 'Understanding hypothyroidism and hyperthyroidism',
            content: {
                overview: 'The thyroid gland produces hormones that regulate metabolism. Disorders include hypothyroidism (underactive) and hyperthyroidism (overactive).',
                symptoms: ['Hypothyroidism: fatigue, weight gain, cold sensitivity, dry skin, constipation', 'Hyperthyroidism: weight loss, rapid heartbeat, anxiety, tremors, heat sensitivity'],
                causes: ['Autoimmune diseases', 'Iodine deficiency or excess', 'Thyroid nodules', 'Medications', 'Radiation therapy'],
                treatment: ['Hypothyroidism: thyroid hormone replacement (levothyroxine)', 'Hyperthyroidism: anti-thyroid medications, radioactive iodine, surgery'],
                prevention: ['Adequate iodine intake', 'Regular thyroid checks if at risk', 'Awareness of family history'],
                whenToSeek: 'Seek care if you notice significant changes in weight, energy levels, or heart rate.'
            }
        }
    ],
    mental_health: [
        {
            id: 'depression',
            title: 'Depression',
            summary: 'Understanding and treating depression',
            content: {
                overview: 'Depression is more than feeling sad. It is a persistent low mood that affects daily life and can be treated effectively.',
                symptoms: ['Persistent sadness', 'Loss of interest in activities', 'Changes in appetite and sleep', 'Fatigue', 'Difficulty concentrating', 'Feelings of worthlessness', 'Thoughts of self-harm'],
                causes: ['Brain chemistry', 'Genetics', 'Life events', 'Medical conditions', 'Medications', 'Substance abuse'],
                treatment: ['Talking therapies (CBT, counseling)', 'Antidepressant medications', 'Exercise', 'Support groups', 'Lifestyle changes'],
                prevention: ['Regular exercise', 'Maintaining social connections', 'Stress management', 'Adequate sleep', 'Limiting alcohol'],
                whenToSeek: 'Seek immediate help if you have thoughts of self-harm or suicide. Contact a crisis helpline or emergency services.'
            }
        },
        {
            id: 'anxiety',
            title: 'Anxiety Disorders',
            summary: 'Managing anxiety and panic attacks',
            content: {
                overview: 'Anxiety disorders involve excessive fear or worry that interferes with daily activities. They are among the most common mental health conditions.',
                symptoms: ['Excessive worry', 'Restlessness', 'Difficulty concentrating', 'Sleep problems', 'Physical symptoms (racing heart, sweating, trembling)'],
                causes: ['Genetics', 'Brain chemistry', 'Personality', 'Life events', 'Medical conditions'],
                treatment: ['Cognitive behavioral therapy (CBT)', 'Exposure therapy', 'Medications (SSRIs, benzodiazepines)', 'Relaxation techniques', 'Lifestyle changes'],
                prevention: ['Regular exercise', 'Healthy sleep habits', 'Limiting caffeine and alcohol', 'Stress management techniques', 'Mindfulness and meditation'],
                whenToSeek: 'Seek help if anxiety is affecting your daily life, relationships, or work.'
            }
        }
    ]
};

const procedureLeaflets = [
    {
        id: 'blood-test',
        title: 'Blood Tests',
        summary: 'What to expect before, during, and after',
        content: {
            overview: 'Blood tests are routine procedures used to check your general health and diagnose conditions.',
            preparation: ['Some tests require fasting (no food/drink except water for 8-12 hours)', 'Continue taking medications unless advised otherwise', 'Wear loose clothing', 'Stay hydrated'],
            procedure: ['A tourniquet is placed on your arm', 'The area is cleaned with antiseptic', 'A needle is inserted into a vein', 'Blood is drawn into tubes', 'The needle is removed and pressure applied'],
            aftercare: ['Keep the bandage on for a few hours', 'Avoid heavy lifting with that arm', 'Drink plenty of fluids', 'Eat a healthy meal'],
            results: 'Results typically take 1-3 days. Your healthcare provider will contact you with results.'
        }
    },
    {
        id: 'x-ray',
        title: 'X-Ray Examination',
        summary: 'Preparing for and understanding X-rays',
        content: {
            overview: 'X-rays use radiation to create images of structures inside your body, especially bones.',
            preparation: ['Remove jewelry and metal objects', 'Wear comfortable, loose clothing', 'Inform staff if you might be pregnant', 'Some X-rays may require fasting or contrast dye'],
            procedure: ['You will be positioned by a radiographer', 'A lead apron may protect areas not being X-rayed', 'You must stay still while images are taken', 'The procedure is painless and takes minutes'],
            aftercare: ['No special care needed', 'You can eat, drink, and resume normal activities', 'If contrast was used, drink extra fluids'],
            results: 'A radiologist will review the images. Results are usually available within 24-48 hours.'
        }
    },
    {
        id: 'mri-scan',
        title: 'MRI Scan',
        summary: 'What to know about magnetic resonance imaging',
        content: {
            overview: 'MRI uses strong magnetic fields and radio waves to produce detailed images of organs and tissues.',
            preparation: ['Inform staff about any metal implants, pacemakers, or claustrophobia', 'Remove all metal objects including jewelry, watches, and cards', 'You may need to fast for abdominal scans', 'Wear comfortable clothing without metal'],
            procedure: ['You lie on a bed that slides into the scanner', 'The machine makes loud knocking noises', 'You must stay very still', 'You can communicate with staff through an intercom', 'Scans typically take 15-90 minutes'],
            aftercare: ['No recovery time needed', 'Resume normal activities immediately', 'If sedation was used, you may need someone to drive you home'],
            results: 'A radiologist analyzes the images. Results are usually available within a few days.'
        }
    },
    {
        id: 'colonoscopy',
        title: 'Colonoscopy',
        summary: 'Preparation and procedure information',
        content: {
            overview: 'A colonoscopy examines the inside of your large intestine using a flexible camera tube.',
            preparation: ['Follow a special diet 1-3 days before', 'Take bowel preparation medication', 'Clear liquids only the day before', 'Arrange transport home (you cannot drive after sedation)'],
            procedure: ['You will receive sedation', 'A thin flexible tube with a camera is inserted', 'Air is pumped in to see clearly', 'Polyps may be removed during the procedure', 'Takes 30-60 minutes'],
            aftercare: ['Rest for the remainder of the day', 'Avoid alcohol for 24 hours', 'Start with light foods', 'Some bloating and gas is normal', 'Report severe pain, heavy bleeding, or fever'],
            results: 'Your doctor will discuss preliminary findings after the procedure. Biopsy results take 1-2 weeks.'
        }
    }
];

const externalResources = [
    { name: 'NHS Health A-Z', url: 'https://www.nhs.uk/conditions/', description: 'Comprehensive health information from the UK National Health Service' },
    { name: 'Mayo Clinic', url: 'https://www.mayoclinic.org/diseases-conditions', description: 'Expert medical information on diseases and conditions' },
    { name: 'WebMD', url: 'https://www.webmd.com/', description: 'Health information and tools for managing your health' },
    { name: 'MedlinePlus', url: 'https://medlineplus.gov/', description: 'Health information from the US National Library of Medicine' }
];

export default function HealthLibrary() {
    const navigate = useNavigate();
    const { t } = useLanguage();
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedLeaflet, setSelectedLeaflet] = useState(null);
    const [activeTab, setActiveTab] = useState('conditions');

    // Read search parameter from URL on mount
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const searchQuery = params.get('search') || params.get('condition');
        if (searchQuery) {
            setSearchTerm(searchQuery);
            // Try to find and auto-select a matching condition
            const matchingCondition = allConditions.find(l => 
                l.title.toLowerCase().includes(searchQuery.toLowerCase())
            );
            if (matchingCondition) {
                setSelectedLeaflet({ leaflet: matchingCondition, type: 'condition' });
            }
        }
    }, []);

    const allConditions = Object.entries(conditionLeaflets).flatMap(([category, leaflets]) => 
        leaflets.map(l => ({ ...l, category }))
    );

    const filteredConditions = allConditions.filter(l => 
        l.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        l.summary.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const filteredProcedures = procedureLeaflets.filter(l =>
        l.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        l.summary.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const categoryIcons = {
        cardiovascular: Heart,
        respiratory: Activity,
        endocrine: Pill,
        mental_health: Brain
    };

    const renderLeafletDetail = (leaflet, type) => {
        const content = leaflet.content;
        
        return (
            <div className="space-y-6">
                <Button variant="ghost" onClick={() => setSelectedLeaflet(null)} className="mb-4">
                    <ArrowLeft className="w-4 h-4 mr-2" /> {t('backToLibrary') || 'Back to Library'}
                </Button>
                
                <div>
                    <h2 className="font-heading text-2xl font-bold mb-2">{leaflet.title}</h2>
                    <p className="text-muted-foreground">{leaflet.summary}</p>
                </div>

                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg">{t('overview') || 'Overview'}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p>{content.overview}</p>
                    </CardContent>
                </Card>

                {type === 'condition' ? (
                    <>
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg">{t('symptoms') || 'Symptoms'}</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <ul className="list-disc list-inside space-y-1">
                                    {content.symptoms.map((s, i) => <li key={i}>{s}</li>)}
                                </ul>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg">{t('causesRiskFactors') || 'Causes & Risk Factors'}</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <ul className="list-disc list-inside space-y-1">
                                    {content.causes.map((c, i) => <li key={i}>{c}</li>)}
                                </ul>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg">{t('treatmentOptions') || 'Treatment Options'}</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <ul className="list-disc list-inside space-y-1">
                                    {content.treatment.map((item, i) => <li key={i}>{item}</li>)}
                                </ul>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg">{t('prevention') || 'Prevention'}</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <ul className="list-disc list-inside space-y-1">
                                    {content.prevention.map((p, i) => <li key={i}>{p}</li>)}
                                </ul>
                            </CardContent>
                        </Card>

                        <Card className="border-amber-200 bg-amber-50 dark:bg-amber-950/20">
                            <CardHeader>
                                <CardTitle className="text-lg text-amber-800 dark:text-amber-200">{t('whenToSeekMedicalHelp') || 'When to Seek Medical Help'}</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="text-amber-700 dark:text-amber-300">{content.whenToSeek}</p>
                            </CardContent>
                        </Card>
                    </>
                ) : (
                    <>
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg">{t('preparation') || 'Preparation'}</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <ul className="list-disc list-inside space-y-1">
                                    {content.preparation.map((p, i) => <li key={i}>{p}</li>)}
                                </ul>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg">{t('theProcedure') || 'The Procedure'}</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <ul className="list-disc list-inside space-y-1">
                                    {content.procedure.map((p, i) => <li key={i}>{p}</li>)}
                                </ul>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg">{t('aftercare') || 'Aftercare'}</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <ul className="list-disc list-inside space-y-1">
                                    {content.aftercare.map((a, i) => <li key={i}>{a}</li>)}
                                </ul>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg">{t('results') || 'Results'}</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p>{content.results}</p>
                            </CardContent>
                        </Card>
                    </>
                )}

                <p className="text-sm text-muted-foreground italic">
                    {t('forGeneralGuidance') || 'This information is for general guidance only. Always consult your healthcare provider for personalized medical advice.'}
                </p>
            </div>
        );
    };

    if (selectedLeaflet) {
        return (
            <div className="min-h-screen bg-background py-8 lg:py-12">
                <div className="max-w-3xl mx-auto px-4">
                    {renderLeafletDetail(selectedLeaflet.leaflet, selectedLeaflet.type)}
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background py-8 lg:py-12" data-testid="health-library-page">
            <div className="max-w-4xl mx-auto px-4">
                <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4">
                    <ArrowLeft className="w-4 h-4 mr-2" /> {t('backToDashboard') || 'Back to Dashboard'}
                </Button>
                
                <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                        <BookOpen className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                        <h1 className="font-heading text-3xl font-bold">{t('healthLibrary')}</h1>
                        <p className="text-muted-foreground">{t('patientInfoLeaflets') || 'Patient information leaflets and health resources'}</p>
                    </div>
                </div>

                {/* Search */}
                <div className="relative my-6">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input 
                        placeholder={t('searchConditions') || 'Search conditions, procedures...'}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                    />
                </div>

                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="grid grid-cols-3 mb-6">
                        <TabsTrigger value="conditions">
                            <Stethoscope className="w-4 h-4 mr-2" />
                            {t('conditions') || 'Conditions'}
                        </TabsTrigger>
                        <TabsTrigger value="procedures">
                            <FileText className="w-4 h-4 mr-2" />
                            {t('procedures') || 'Procedures'}
                        </TabsTrigger>
                        <TabsTrigger value="resources">
                            <ExternalLink className="w-4 h-4 mr-2" />
                            {t('resources') || 'Resources'}
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="conditions">
                        <div className="grid gap-4">
                            {filteredConditions.map((leaflet) => {
                                const Icon = categoryIcons[leaflet.category] || Activity;
                                return (
                                    <Card 
                                        key={leaflet.id} 
                                        className="cursor-pointer hover:border-primary/50 transition-colors"
                                        onClick={() => setSelectedLeaflet({ leaflet, type: 'condition' })}
                                    >
                                        <CardContent className="p-4 flex items-center gap-4">
                                            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                                                <Icon className="w-5 h-5 text-primary" />
                                            </div>
                                            <div className="flex-1">
                                                <h3 className="font-semibold">{leaflet.title}</h3>
                                                <p className="text-sm text-muted-foreground">{leaflet.summary}</p>
                                            </div>
                                            <ChevronRight className="w-5 h-5 text-muted-foreground" />
                                        </CardContent>
                                    </Card>
                                );
                            })}
                            {filteredConditions.length === 0 && (
                                <p className="text-center text-muted-foreground py-8">No conditions found matching your search.</p>
                            )}
                        </div>
                    </TabsContent>

                    <TabsContent value="procedures">
                        <div className="grid gap-4">
                            {filteredProcedures.map((leaflet) => (
                                <Card 
                                    key={leaflet.id} 
                                    className="cursor-pointer hover:border-primary/50 transition-colors"
                                    onClick={() => setSelectedLeaflet({ leaflet, type: 'procedure' })}
                                >
                                    <CardContent className="p-4 flex items-center gap-4">
                                        <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900 flex items-center justify-center flex-shrink-0">
                                            <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="font-semibold">{leaflet.title}</h3>
                                            <p className="text-sm text-muted-foreground">{leaflet.summary}</p>
                                        </div>
                                        <ChevronRight className="w-5 h-5 text-muted-foreground" />
                                    </CardContent>
                                </Card>
                            ))}
                            {filteredProcedures.length === 0 && (
                                <p className="text-center text-muted-foreground py-8">No procedures found matching your search.</p>
                            )}
                        </div>
                    </TabsContent>

                    <TabsContent value="resources">
                        <div className="grid gap-4">
                            {externalResources.map((resource, i) => (
                                <Card 
                                    key={i} 
                                    className="cursor-pointer hover:border-primary/50 transition-colors"
                                    onClick={() => window.open(resource.url, '_blank')}
                                >
                                    <CardContent className="p-4 flex items-center gap-4">
                                        <div className="w-10 h-10 rounded-xl bg-green-100 dark:bg-green-900 flex items-center justify-center flex-shrink-0">
                                            <ExternalLink className="w-5 h-5 text-green-600 dark:text-green-400" />
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="font-semibold">{resource.name}</h3>
                                            <p className="text-sm text-muted-foreground">{resource.description}</p>
                                        </div>
                                        <ExternalLink className="w-5 h-5 text-muted-foreground" />
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
}
